import React, { useEffect, useState } from "react";
import {
	Avatar,
	Box,
	Button,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import AddItemDialog from "./AddItemDialog";
import AddDrinkDialog from "./AddDrinkDialog";
import { getSalesMenu } from "../../../../../../../services/Admin/SalesScreenApi";
import { getAiDrinks } from "../../../../../../../services/Admin/AiDrinksApi";
import { getImageUrl } from "../../../../../../../services/indexMediaBaseUrl";

const ITEM_STATUSES = {
	pending: "جديد لم يحضر",
	in_preparation: "في التحضير",
	in_prepration: "في التحضير",
	pickup: "تم الاستلام من العميل",
	ready: "جاهز",
};

const OrderItemsForm = ({
	items,
	handleItemChange,
	handleAddItem,
	handleRemoveItem,
	loading,
}) => {
	const [productDialogOpen, setProductDialogOpen] = useState(false);
	const [drinkDialogOpen, setDrinkDialogOpen] = useState(false);
	const [productsMap, setProductsMap] = useState({});
	const [drinksMap, setDrinksMap] = useState({});

	// جلب بيانات المنتجات والمشروبات عند التحميل
	useEffect(() => {
		const fetchData = async () => {
			try {
				// جلب المنتجات
				const menuRes = await getSalesMenu();
				if (Array.isArray(menuRes?.data)) {
					const prodMap = {};
					menuRes.data.forEach((category) => {
						if (Array.isArray(category.products)) {
							category.products.forEach((product) => {
								prodMap[product.id] = {
									...product,
									categoryName: category.name,
								};
							});
						}
					});
					setProductsMap(prodMap);
				}

				// جلب المشروبات
				const drinksRes = await getAiDrinks();
				if (Array.isArray(drinksRes?.data)) {
					const drinkMap = {};
					drinksRes.data.forEach((drink) => {
						drinkMap[drink.id] = drink;
					});
					setDrinksMap(drinkMap);
				}
			} catch (err) {
				console.error("فشل تحميل البيانات:", err);
			}
		};

		fetchData();
	}, []);

	const handleOpenProductDialog = () => {
		setProductDialogOpen(true);
	};

	const handleCloseProductDialog = () => {
		setProductDialogOpen(false);
	};

	const handleOpenDrinkDialog = () => {
		setDrinkDialogOpen(true);
	};

	const handleCloseDrinkDialog = () => {
		setDrinkDialogOpen(false);
	};

	const handleAddItemFromDialog = (itemData) => {
		handleAddItem(itemData);
		// الديالوج يبقى مفتوحاً للسماح بإضافة عناصر أخرى
	};

	const handleAddDrinkFromDialog = (itemData) => {
		handleAddItem(itemData);
		// الديالوج يبقى مفتوحاً للسماح بإضافة عناصر أخرى
	};

	// الحصول على بيانات المنتج من الخريطة
	const getProductData = (productId) => {
		return productsMap[productId] || null;
	};

	// الحصول على بيانات المشروب من الخريطة
	const getDrinkData = (drinkId) => {
		return drinksMap[drinkId] || null;
	};

	return (
		<Box sx={{ mt: 3 }}>
			{/* أزرار الإضافة */}
			<Box sx={{ display: "flex", gap: 2, mb: 3 }}>
				<Button
					variant="contained"
					startIcon={<AddCircleIcon />}
					onClick={handleOpenProductDialog}
					disabled={loading}
				>
					إضافة منتج
				</Button>
				<Button
					variant="contained"
					color="secondary"
					startIcon={<AddCircleIcon />}
					onClick={handleOpenDrinkDialog}
					disabled={loading}
				>
				مشروبات العملاء
				</Button>
			</Box>

			{/* جدول العناصر */}
			{items.length > 0 ? (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead sx={{ backgroundColor: "#f5f5f5" }}>
							<TableRow>
								<TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
									البيان
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: "bold" }}>
									النوع
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: "bold" }}>
									الكمية
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: "bold" }}>
									السعر
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: "bold" }}>
									الإجمالي
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: "bold" }}>
									الملاحظة
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: "bold" }}>
									الحالة
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: "bold" }}>
									الإجراء
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{items.map((item, index) => {
								const total = (item.price || 0) * (item.quantity || 0);
								
								// الحصول على بيانات المنتج أو المشروب
								let itemLabel = "غير محدد";
								let itemImage = "";
								let itemType = "";
								
								if (item.product_id) {
									const product = getProductData(item.product_id);
									if (product) {
										itemLabel = product.name;
										itemImage = product.image ? getImageUrl(product.image) : "";
										itemType = "من منيو";
									} else {
										itemLabel = `من منيو #${item.product_id}`;
										itemType = "من منيو";
									}
								} else if (item.drink_id) {
									const drink = getDrinkData(item.drink_id);
									if (drink) {
										itemLabel = drink.name;
										itemType = "مخصص";
									} else {
										itemLabel = `مخصص #${item.drink_id}`;
										itemType = "مخصص";
									}
								}

								return (
									<TableRow
										key={index}
										sx={{
											"&:hover": {
												backgroundColor: "#f9f9f9",
											},
										}}
									>
										<TableCell sx={{ textAlign: "right" }}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												{itemImage && (
													<Avatar
														src={itemImage}
														alt={itemLabel}
														sx={{ width: 40, height: 40 }}
													/>
												)}
												<Typography variant="body2">{itemLabel}</Typography>
											</Box>
										</TableCell>
										<TableCell align="center">{itemType}</TableCell>
										<TableCell align="center">{item.quantity}</TableCell>
										<TableCell align="center">
											{Number(item.price).toFixed(2)} جنيه
										</TableCell>
										<TableCell align="center" sx={{ fontWeight: "bold" }}>
											{total.toFixed(2)} جنيه
										</TableCell>
										<TableCell align="center" sx={{ maxWidth: 150 }}>
											<Typography variant="caption" noWrap>
												{item.note || "-"}
											</Typography>
										</TableCell>
										<TableCell align="center">
											{ITEM_STATUSES[item.status] || item.status}
										</TableCell>
										<TableCell align="center">
											<IconButton
												size="small"
												color="error"
												onClick={() => handleRemoveItem(index)}
												disabled={loading}
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			) : (
				<Paper sx={{ p: 3, textAlign: "center", backgroundColor: "#f9f9f9" }}>
					<Typography color="textSecondary">
						لم يتم إضافة أي عناصر بعد. اضغط على "إضافة منتج" أو "إضافة مخصص" للبدء
					</Typography>
				</Paper>
			)}

			{/* ملخص الإجمالي */}
			{items.length > 0 && (
				<Box
					sx={{
						mt: 2,
						p: 2,
						backgroundColor: "#f5f5f5",
						borderRadius: 1,
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography sx={{ fontWeight: "bold", fontSize: 18 }}>
						إجمالي الفاتورة
					</Typography>
					<Typography
						sx={{
							fontWeight: "bold",
							fontSize: 20,
							color: "primary.main",
						}}
					>
						{items
							.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0)
							.toFixed(2)}{" "}
						جنيه
					</Typography>
				</Box>
			)}

			{/* ديالوج إضافة المنتج */}
			<AddItemDialog
				open={productDialogOpen}
				onClose={handleCloseProductDialog}
				itemType="product"
				onAdd={handleAddItemFromDialog}
			/>

			{/* ديالوج إضافة المشروب */}
			<AddDrinkDialog
				open={drinkDialogOpen}
				onClose={handleCloseDrinkDialog}
				onAdd={handleAddDrinkFromDialog}
			/>
		</Box>
	);
};

export default OrderItemsForm;
